import React, {
	useRef,
	useCallback,
	useEffect,
	type ComponentType,
	type RefObject,
	useState,
	useMemo,
} from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import type { CSSToken } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { EditViewPopupModifiersProvider } from '@atlassian/jira-issue-field-inline-edit-lite/src/common/controllers/modifiers-context/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { Props as AsyncColumnPickerProps } from '@atlassian/jira-issue-table-column-picker/src/ui/ColumnConfigWithContainer.tsx';
import { getFlatListKey } from '@atlassian/jira-issue-table-hierarchy/src/common/utils/get-flat-list-key/index.tsx';
import {
	useFlatList,
	useIssueIds,
	useChildIssueIds,
	useFlatListIssueKeys,
	getShowMoreItemKey,
	type PopulateConnectionArgs,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import { HoverPopoverWithDelegator } from '@atlassian/jira-hover-popover/src/ui/popover-with-delegator/index.tsx';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import type { context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef$key as FieldConfigKey } from '../../common/ui/inline-field-config/context/__generated__/context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef.graphql';
import { getInlineEditModifier } from '../../common/utils/get-inline-edit-modifiers/index.tsx';
import { useScrollStateActions } from '../../controllers/scroll-state/index.tsx';

import type {
	NoColumnsProps,
	Column,
	ColumnId,
	TableInjectedCellProps,
	NoIssuesProps,
	TopLevelIssueCreateConfig,
} from '../../common/types.tsx';
import {
	ACTION_COLUMNS,
	COLUMN_RESIZE_HANDLE_FULL_HEIGHT,
	COLUMN_RESIZE_HANDLE_HEADER_ONLY,
	ROW_HEIGHT,
	HEADER_HEIGHT,
} from '../../common/constants.tsx';

import { Overlay, OverlayOld } from '../../common/ui/overlay/index.tsx';
import {
	useIsFixedTableLayoutEnabled,
	useIsVirtualizationEnabled,
	useIsMergedColumnVisible,
	useIsConditionalFormattingEnabled,
	useIsSingleLineRowHeightEnabled,
	useIsTimelineEnabled,
	useIsStickyColumnsVisible,
} from '../../controllers/features/selectors.tsx';
import { VirtualizationContainer } from '../../controllers/virtualization/rss-store.tsx';
import type { IFCConfigType } from '../../common/ui/inline-field-config/types.tsx';
import { IFCConfigContextProviderComponent } from '../../common/ui/inline-field-config/context/index.tsx';
import { useEventDelegator } from '../../controllers/use-event-delegator/index.tsx';
import { useTimelineCustomComponents } from '../../controllers/timeline-custom-components/index.tsx';
import type { IssueTable_rules$key as FormattingRulesFragment } from './__generated__/IssueTable_rules.graphql';
import type { IssueTableIssueTable$key } from './__generated__/IssueTableIssueTable.graphql';
import type { IssueTable_rankField$key as RankFieldFragment } from './__generated__/IssueTable_rankField.graphql';
import type { IssueTable_project$key as ProjectFragment } from './__generated__/IssueTable_project.graphql';
import type { IssueTable_issueConnection$key as IssueResultsFragment } from './__generated__/IssueTable_issueConnection.graphql';
import type { IssueTable_spreadsheetGroupConnection$key as GroupResultsFragment } from './__generated__/IssueTable_spreadsheetGroupConnection.graphql';
import GroupTableBody from './group-table-body/GroupTableBody.tsx';
import { Header } from './header/HeaderCellContainer.tsx';
import messages from './messages.tsx';
import NoColumnsRenderer from './no-columns-renderer/index.tsx';
import NoIssuesRenderer from './no-issues-renderer/index.tsx';
import TableBody from './table-body/TableBody.tsx';
import { InitializeVirtualizer as DefaultInitializeVirtualizerOld } from './initialize-virtualizer/index.tsx';
import { InitializeVirtualizer as DefaultInitializeVirtualizerNew } from './initialize-virtualizer-new/InitializeVirtualizer.tsx';

const emptyArray: string[] = [];

// We use rowsCount=50 by default to set the minimum table height until virtualizer reads it from DOM
const getInitialTableHeight = (rowsCount = 50) => ROW_HEIGHT * rowsCount + HEADER_HEIGHT;

export type Props = {
	view?: IssueTableIssueTable$key | null;
	columns: Column[];
	NoIssues?: ComponentType<NoIssuesProps>;
	canViewColumnConfiguration: boolean;
	canEditColumnConfiguration: boolean;
	loading: boolean;
	issueResults: IssueResultsFragment | null;
	groupResults: GroupResultsFragment | null;
	project: ProjectFragment | null;
	rankField: RankFieldFragment | null;
	NoColumns?: ComponentType<NoColumnsProps>;
	onColumnsChange?: (columns: Column[]) => void;
	onResizeColumn?: (columnId: ColumnId, width: number | undefined) => void;
	onFreezeColumn?: (updatedColumns: { fieldSetId: string; isFrozen: boolean }[]) => void;
	/** @deprecated remove when jira_list_reduce_re_renders is cleaned up */
	onIssueFlatListUpdated?: (
		newFlatKeyList: string[],
		newFlatIdList: string[],
		newFlatChildIdList: string[],
	) => void;
	tableInjectedProps: TableInjectedCellProps;
	tableRef: RefObject<HTMLTableElement>;
	isGroupingSupported?: boolean;
	isCompactColumnEnabled?: boolean;
	tableBorderRadius?: CSSToken;
	columnPickerProps?: AsyncColumnPickerProps;
	ifcConfig?: IFCConfigType;
	columnsQueryRef?: FieldConfigKey;
	enableDynamicOrderForMergedColumn?: boolean;
	isDraggingRowFaded?: boolean;
	// TODO: Make this required when refactor_issue_table_for_timline is being cleaned up
	mergedColumnMinWidth?: number;
	tableRole?: string;
	formattingRules?: FormattingRulesFragment | null;
	rowInlineCreateCanShowAdditionalFields: boolean;
	resizeHandleType?:
		| typeof COLUMN_RESIZE_HANDLE_FULL_HEIGHT
		| typeof COLUMN_RESIZE_HANDLE_HEADER_ONLY;
	topLevelIssueCreateConfig?: TopLevelIssueCreateConfig;
	initialPopulateConnectionArgs?: PopulateConnectionArgs[];
};

const IssueTable = ({
	view,
	columnPickerProps,
	columns,
	NoIssues,
	canViewColumnConfiguration,
	canEditColumnConfiguration,
	issueResults,
	groupResults,
	project,
	rankField,
	loading,
	NoColumns,
	onColumnsChange,
	onResizeColumn,
	onFreezeColumn,
	tableInjectedProps,
	tableRef,
	isGroupingSupported,
	isCompactColumnEnabled = false,
	onIssueFlatListUpdated,
	tableBorderRadius,
	ifcConfig,
	columnsQueryRef,
	enableDynamicOrderForMergedColumn,
	isDraggingRowFaded,
	mergedColumnMinWidth,
	tableRole,
	formattingRules,
	rowInlineCreateCanShowAdditionalFields,
	resizeHandleType,
	topLevelIssueCreateConfig,
	initialPopulateConnectionArgs,
}: Props) => {
	const { formatMessage } = useIntl();
	const [resizingColumnIndex, setResizingColumnIndex] = useState<number>();
	const isConditionalFormattingEnabled =
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		fg('jira_list_conditional_formatting') && useIsConditionalFormattingEnabled();
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();

	const timelineCustomComponents = useTimelineCustomComponents();

	const InitializeVirtualizer =
		timelineCustomComponents?.InitializeVirtualizer ??
		(fg('jira_list_reduce_re_renders')
			? DefaultInitializeVirtualizerNew
			: DefaultInitializeVirtualizerOld);

	const viewData = useFragment<IssueTableIssueTable$key>(
		graphql`
			fragment IssueTableIssueTable on JiraIssueSearchViewMetadata {
				...HeaderCellContainer
			}
		`,
		view ?? null,
	);

	const issueResultsData = useFragment<IssueResultsFragment>(
		graphql`
			fragment IssueTable_issueConnection on JiraIssueConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isReparentingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
				isMimicFlatlistRssWhileUninitialisedEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/mimic-flatlist-rss-while-uninitialised.relayprovider"
				}
				isJiraListReduceRerendersEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-list-reduce-rerenders.relayprovider"
				}
			) {
				edges {
					node {
						issueId @include(if: $isMimicFlatlistRssWhileUninitialisedEnabled)
					}
					__typename # only interested in edge presence to handle empty state
				}
				totalIssueSearchResultCount
				...TableBodyTableBody
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isReparentingEnabled: $isReparentingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...context_nativeIssueTable_IFCConfigContextProvider_queryRef
				...InitializeVirtualizer_issueResults @include(if: $isJiraListReduceRerendersEnabled)
			}
		`,
		issueResults,
	);

	const groupResultsData = useFragment<GroupResultsFragment>(
		graphql`
			fragment IssueTable_spreadsheetGroupConnection on JiraSpreadsheetGroupConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				isPaginating: { type: "Boolean", defaultValue: false }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				viewConfigInput: { type: "JiraIssueSearchViewConfigInput" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
				isMimicFlatlistRssWhileUninitialisedEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/mimic-flatlist-rss-while-uninitialised.relayprovider"
				}
				isJiraListReduceRerendersEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-list-reduce-rerenders.relayprovider"
				}
			) {
				edges {
					node {
						id
					}
				}
				firstGroup
					@skip(if: $isPaginating)
					@include(if: $isMimicFlatlistRssWhileUninitialisedEnabled) {
					id
					issues(
						viewConfigInput: $viewConfigInput
						fieldSetsInput: $fieldSetsInput
						scope: $fieldSetsScope
						first: 50
						settings: $settings
					) {
						edges {
							node {
								issueId
							}
						}
						pageInfo {
							hasNextPage
						}
					}
				}
				...GroupTableBody
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isDensityFull: $isDensityFull
						isPaginating: $isPaginating
						projectKeys: $projectKeys
						projectKey: $projectKey
						viewConfigInput: $viewConfigInput
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...InitializeVirtualizer_groupResults
					@arguments(isPaginating: $isPaginating)
					@include(if: $isJiraListReduceRerendersEnabled)
			}
		`,
		groupResults,
	);

	const projectData = useFragment<ProjectFragment>(
		graphql`
			fragment IssueTable_project on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				...GroupTableBody_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
				...TableBody_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project,
	);

	const formattingRulesData = useFragment<FormattingRulesFragment>(
		graphql`
			fragment IssueTable_rules on JiraFormattingRuleConnection {
				...GroupTableBody_rules
				...TableBody_rules
			}
		`,
		formattingRules,
	);

	const rankFieldData = useFragment<RankFieldFragment>(
		graphql`
			fragment IssueTable_rankField on JiraJqlFieldWithAliases {
				...TableBody_rankField
				...GroupTableBody_rankField
			}
		`,
		rankField,
	);

	let flatList = emptyArray;
	if (!fg('jira_list_reduce_re_renders')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		flatList = useFlatList();

		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const flatListIssueKeys = useFlatListIssueKeys();
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const flatListIssueIds = useIssueIds();
		const flatListChildIssueIds = expVal(
			'bulk_ops_select_all_beyond_experiment',
			'isEnabled',
			false,
		)
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useChildIssueIds()
			: [];
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onIssueFlatListUpdated?.(
				flatListIssueKeys.filter(Boolean),
				flatListIssueIds,
				flatListChildIssueIds,
			);
		}, [
			flatList,
			flatListIssueIds,
			flatListIssueKeys,
			onIssueFlatListUpdated,
			flatListChildIssueIds,
		]);
	}

	// initialFlatList is currently only guaranteed to keep virtualiser props stable while flatList is empty
	// If flatList is no longer empty, initialFlatList should be ignored
	const initialFlatList = useMemo(() => {
		if (!(flatList?.length <= 0 && fg('mimic_flatlist_rss_while_uninitialised'))) {
			return [];
		}

		if (issueResultsData && issueResultsData.edges) {
			return issueResultsData.edges
				.map((edge) => edge?.node?.issueId)
				.filter(Boolean)
				.map((issueId) => getFlatListKey({ itemId: issueId }));
		}
		if (groupResultsData && groupResultsData.firstGroup && groupResultsData?.firstGroup?.id) {
			const firstGroupId = groupResultsData.firstGroup.id;
			return [
				getFlatListKey({
					itemId: firstGroupId,
					groupId: firstGroupId,
				}),
				...(groupResultsData.firstGroup.issues?.edges ?? [])
					.map((edge) => edge?.node?.issueId)
					.filter(Boolean)
					.map((issueId) => getFlatListKey({ itemId: issueId, groupId: firstGroupId })),
				...(groupResultsData.firstGroup.issues?.pageInfo?.hasNextPage
					? [
							getFlatListKey({
								itemId: getShowMoreItemKey(firstGroupId),
								groupId: firstGroupId,
							}),
						]
					: []),
				...(groupResultsData.edges ?? [])
					.map((edge) => edge?.node?.id)
					.filter((id) => id !== firstGroupId)
					.filter(Boolean)
					.map((id) => getFlatListKey({ itemId: id, groupId: id })),
			];
		}
		return [];
	}, [flatList?.length, groupResultsData, issueResultsData]);

	// Represents a stable list of item keys between first render (i.e. flat list is empty) and after flat list has been initialised in RSS.
	const readyFlatList: string[] =
		flatList?.length === 0 && fg('mimic_flatlist_rss_while_uninitialised')
			? initialFlatList
			: flatList;

	/** @deprecated remove when jira_list_reduce_re_renders is cleaned up */
	const getItemKey = useCallback((index: number) => readyFlatList[index] ?? index, [readyFlatList]);

	/** @deprecated remove when jira_list_reduce_re_renders is cleaned up */
	const totalSizeRef = useRef<number>(
		getInitialTableHeight(
			fg('mimic_flatlist_rss_while_uninitialised')
				? readyFlatList.length
				: issueResultsData?.edges?.length,
		),
	);

	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();

	const showNoIssues = issueResultsData && !issueResultsData.edges?.length;
	const showNoGroupingIssues =
		isGroupingSupported && groupResultsData && !groupResultsData.edges?.length;

	let isMergedColumnVisible = false;
	if (fg('jira_list_merged_column_gate')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isMergedColumnVisible = useIsMergedColumnVisible();
	}

	const showNoColumns =
		columns.filter((col) => !ACTION_COLUMNS.includes(col.id)).length === 0 &&
		!isMergedColumnVisible;
	const { getScrollInfo } = useScrollStateActions();
	let isStickyColumnsVisible = false;
	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('issue_table_frozen_columns') && fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isStickyColumnsVisible = useIsStickyColumnsVisible();
	}

	const modifier = useMemo(
		() =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('issue_table_frozen_columns') && fg('jira_list_frozen_column_bugfix')
				? getInlineEditModifier({ columns, getScrollInfo, isStickyColumnsVisible })
				: () => [],
		[columns, getScrollInfo, isStickyColumnsVisible],
	);

	const isVirtualizationEnabled = useIsVirtualizationEnabled();

	const { handleMouseOver, handleMouseLeave } = useEventDelegator();

	const isPageHydrating = useIsHydrateRenderMode();

	const resizeHandler =
		resizeHandleType ??
		(showNoIssues || showNoColumns
			? COLUMN_RESIZE_HANDLE_HEADER_ONLY
			: COLUMN_RESIZE_HANDLE_FULL_HEIGHT);

	const resizeHandlerOld =
		(showNoIssues || showNoColumns) && !isTimelineEnabled
			? COLUMN_RESIZE_HANDLE_HEADER_ONLY
			: COLUMN_RESIZE_HANDLE_FULL_HEIGHT;

	const tableHeader = (
		<IFCConfigContextProviderComponent
			ifcConfig={ifcConfig}
			columns={columns}
			onColumnsChange={columnPickerProps?.onChange}
			queryRef={issueResultsData ?? null}
			columnsQueryRef={columnsQueryRef ?? null}
		>
			<Header
				view={viewData}
				columnPickerProps={columnPickerProps}
				columns={columns}
				canViewColumnConfiguration={canViewColumnConfiguration}
				canEditColumnConfiguration={canEditColumnConfiguration}
				isCompactColumnEnabled={isCompactColumnEnabled}
				resizeHandleType={fg('refactor_issue_table_for_timline') ? resizeHandler : resizeHandlerOld}
				onColumnsChange={onColumnsChange}
				onResizeColumn={onResizeColumn}
				onFreezeColumn={onFreezeColumn}
				tableRef={tableRef}
				tableBorderRadius={tableBorderRadius}
				showNoColumns={showNoColumns}
				{...(fg('issue_table_frozen_columns') && {
					resizingColumnIndex,
					setResizingColumnIndex,
				})}
				enableDynamicOrderForMergedColumn={enableDynamicOrderForMergedColumn}
				mergedColumnMinWidth={mergedColumnMinWidth}
			/>
		</IFCConfigContextProviderComponent>
	);

	if (showNoIssues || showNoGroupingIssues) {
		return (
			<>
				<Table
					isFixedTableLayoutEnabled={isFixedTableLayoutEnabled}
					aria-label={formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.issuesTableLabelIssueTermRefresh
							: messages.issuesTableLabel,
					)}
					ref={tableRef}
				>
					{tableHeader}
				</Table>
				{!isTimelineEnabled && <NoIssuesRenderer NoIssues={NoIssues} isLoading={loading} />}
			</>
		);
	}

	if (showNoColumns) {
		return (
			<>
				{canViewColumnConfiguration && (
					<Table
						isFixedTableLayoutEnabled={isFixedTableLayoutEnabled}
						aria-label={formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.issuesTableLabelIssueTermRefresh
								: messages.issuesTableLabel,
						)}
						ref={tableRef}
					>
						{tableHeader}
					</Table>
				)}
				<NoColumnsRenderer
					NoColumns={NoColumns}
					hasColumnConfiguration={canViewColumnConfiguration}
					isLoading={loading}
				/>
			</>
		);
	}

	const renderTableBody = () => {
		if (isGroupingSupported && groupResultsData && !issueResultsData) {
			return (
				<GroupTableBody
					groupResults={groupResultsData}
					rankField={rankFieldData ?? null}
					columns={columns}
					tableInjectedProps={tableInjectedProps}
					project={projectData ?? null}
					loading={loading}
					isConditionalFormattingEnabled={isConditionalFormattingEnabled}
					formattingRules={formattingRulesData ?? null}
					rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
					{...(fg('issue_table_frozen_columns') && {
						resizingColumnIndex,
					})}
				/>
			);
		}

		if (issueResultsData == null) return null;
		const tableBody = (
			<TableBody
				topLevelIssueCreateConfig={topLevelIssueCreateConfig}
				issueResults={issueResultsData}
				columns={columns}
				tableInjectedProps={tableInjectedProps}
				project={projectData ?? null}
				rankField={rankFieldData ?? null}
				isGroupingSupported={isGroupingSupported}
				isConditionalFormattingEnabled={isConditionalFormattingEnabled}
				isDraggingRowFaded={isDraggingRowFaded}
				formattingRules={formattingRulesData ?? null}
				{...(fg('issue_table_frozen_columns') && {
					resizingColumnIndex,
				})}
				rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
				initialPopulateConnectionArgs={initialPopulateConnectionArgs}
			/>
		);

		return tableBody;
	};

	const renderTable = (style?: React.CSSProperties) => {
		return (
			<EditViewPopupModifiersProvider modifiers={modifier}>
				<Table
					{...(style ? { style } : {})}
					role={fg('jira_timeline_harmonisation_m1_targeting') ? tableRole : undefined}
					isFixedTableLayoutEnabled={isFixedTableLayoutEnabled}
					aria-label={formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.issuesTableLabelIssueTermRefresh
							: messages.issuesTableLabel,
					)}
					ref={tableRef}
					aria-rowcount={
						issueResultsData?.totalIssueSearchResultCount &&
						issueResultsData?.totalIssueSearchResultCount > 0 &&
						fg('nin-grid-keyboard-navigation')
							? issueResultsData?.totalIssueSearchResultCount
							: undefined
					}
					data-vc={`issue-table${__SERVER__ && !isPageHydrating ? '-ssr' : ''}`}
					onMouseOver={handleMouseOver}
					onMouseLeave={handleMouseLeave}
					{...(__SERVER__ &&
						!isPageHydrating &&
						fg('additional_nin_ssr_placeholders') && {
							'data-ssr-placeholder': 'issue-table-placeholder',
						})}
					{...(!__SERVER__ &&
						!isPageHydrating &&
						fg('additional_nin_ssr_placeholders') && {
							'data-ssr-placeholder-replace': 'issue-table-placeholder',
						})}
				>
					{tableHeader}
					{renderTableBody()}
					{loading ? (
						<TableFooter>
							<tr>
								<td>
									{fg('add_nin_press_interactions') ? (
										<Overlay interactionName="issue-table-renderer-overlay" />
									) : (
										<OverlayOld />
									)}
								</td>
							</tr>
						</TableFooter>
					) : null}
					{isSingleLineRowHeightEnabled && <HoverPopoverWithDelegator />}
				</Table>
			</EditViewPopupModifiersProvider>
		);
	};

	/** @deprecated remove when jira_list_reduce_re_renders is cleaned up */
	// eslint-disable-next-line no-nested-ternary
	const rowsCount = fg('mimic_flatlist_rss_while_uninitialised')
		? readyFlatList.length
		: flatList?.length > 0
			? flatList?.length
			: issueResultsData?.edges?.length;

	if (isVirtualizationEnabled) {
		return (
			<VirtualizationContainer>
				{/* InitializeVirtualizer must come first, otherwise other components may be re-mounted after initialization */}
				<InitializeVirtualizer
					rows={rowsCount ?? 0}
					getItemKey={getItemKey}
					totalSizeRef={totalSizeRef}
					issueResults={issueResultsData}
					groupResults={groupResultsData}
				/>
				{renderTable({
					width: '100%',
					position: 'relative',
				})}
			</VirtualizationContainer>
		);
	}

	return renderTable();
};

export default IssueTable;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Table = styled.table<{ isFixedTableLayoutEnabled: boolean }>(
	{
		// Cross-browser CSS hack to allow inline editable fields to fill the entire space of their `td`. Please do
		// extensive cross-browser testing before you consider updating this rule.
		height: '1px',
		position: 'relative',
		margin: 0,
		// Using separate borders with no border spacing so that sticky table header can maintain borders on scroll
		// Note: having separate borders should also facilitate adding a border radius to the table if desired
		borderCollapse: 'separate',
		borderSpacing: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isFixedTableLayoutEnabled }) =>
		isFixedTableLayoutEnabled && {
			tableLayout: 'fixed',
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export { Table as TableForTesting };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableFooter = styled.tfoot({
	border: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	td: {
		padding: 0,
	},
});
